import type { Permission } from '@app/permissions/permissions';
import {
  getHighestRoleLevel,
  getPermissionsForRoles,
  getPrimaryRole,
  resolveInheritedRoles,
  getAssignableRoles,
  getAccessibleFeatures,
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
  hasMinimumRole,
  canAccessFeature,
  canManageUser,
} from '@app/permissions/permissions.utils';
import type { Feature } from '@app/permissions/role-permissions';
import { useAppSelector } from '@app/store/store';
import type { Role } from '@app/utils/protected-routes.utils';

export function usePermissions() {
  const userRoles = useAppSelector((state) => state.authentication.user?.roles ?? []);

  const allRoles = Array.from(resolveInheritedRoles(userRoles));
  const allPermissions = Array.from(getPermissionsForRoles(userRoles));
  const primaryRole = getPrimaryRole(userRoles);
  const roleLevel = getHighestRoleLevel(userRoles);
  const accessibleFeatures = getAccessibleFeatures(userRoles);
  const assignableRoles = getAssignableRoles(userRoles);

  return {
    userRoles,
    allRoles,
    primaryRole,
    roleLevel,
    allPermissions,
    accessibleFeatures,
    assignableRoles,
    hasPermission: (permission: Permission) => hasPermission(userRoles, permission),
    hasAnyPermission: (permissions: Permission[]) => hasAnyPermission(userRoles, permissions),
    hasAllPermissions: (permissions: Permission[]) => hasAllPermissions(userRoles, permissions),
    hasMinimumRole: (role: Role) => hasMinimumRole(userRoles, role),
    canManageUser: (targetRoles: Role[]) => canManageUser(userRoles, targetRoles),
  };
}

export function useFeaturePermissions<T extends Record<string, Permission>>(
  permissionMap: T,
): Record<keyof T, boolean> {
  const userRoles = useAppSelector((state) => state.authentication.user?.roles ?? []);

  const result: Record<string, boolean> = {};

  for (const [key, permission] of Object.entries(permissionMap)) {
    result[key] = hasPermission(userRoles, permission);
  }

  return result as Record<keyof T, boolean>;
}

export function useIsMinimumRole(role: Role): boolean {
  const userRoles = useAppSelector((state) => state.authentication.user?.roles ?? []);
  return hasMinimumRole(userRoles, role);
}

export function useHasPermission(permission: Permission): boolean {
  const userRoles = useAppSelector((state) => state.authentication.user?.roles ?? []);
  return hasPermission(userRoles, permission);
}

export function useCanAccessFeature(feature: Feature): boolean {
  const userRoles = useAppSelector((state) => state.authentication.user?.roles ?? []);
  return canAccessFeature(userRoles, feature);
}
